import {
  Button,
  CircularProgress,
  Box,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import {
  EditAppBar,
  NotificationTypeEnum,
  useNotification,
  Skeletons,
} from '@prismamedia/one-components';
import React, { FC, useState } from 'react';
import { OrphanPageSelector } from '../../../components/StatusSelector';
import { formatDate } from '../../../utils/dateUtils';
import { BrandKey, OrphanPageStatus } from '../../../__generated__/queries-web';
import {
  prepareUpsertVariable,
  useUpsertOrphanPage,
} from '../graphql/upsertOrphanPage.web.graphql';
import { OrphanEditor } from './OrphanEditor';

interface OrphanEditProps {
  defaultState: EditorState;
}

export interface EditorState {
  id: string;
  status: OrphanPageStatus;
  title: string;
  url: string;
  metas: string[];
  content: string;
  isFullScreen: boolean;
  isCMPEnabled: boolean;
  isAdsEnabled: boolean;
  brandKey: BrandKey;
  publishedAt?: string | null;
}

export const OrphanEdit: FC<OrphanEditProps> = ({ defaultState }) => {
  const [mutation, { loading }] = useUpsertOrphanPage();
  const { pushNotification } = useNotification();
  const [state, setState] = useState<EditorState>(defaultState);

  const doUpsert = () => {
    mutation({
      variables: prepareUpsertVariable({
        id: state.id,
        brandKey: state.brandKey,
        isCMPEnabled: state.isCMPEnabled,
        isAdsEnabled: state.isAdsEnabled,
        isFullScreen: state.isFullScreen,
        metas: state.metas,
        content: state.content,
        title: state.title,
        uri: state.url,
        status: state.status,
        publishedAt:
          state.status === OrphanPageStatus.Published
            ? defaultState.status !== OrphanPageStatus.Published
              ? new Date().toISOString()
              : state.publishedAt
            : null,
      }),
    })
      .then(() => {
        pushNotification({
          message: 'Votre page a bien été sauvegardée',
          type: NotificationTypeEnum.success,
        });
      })
      .catch((err) => {
        const isUriError = err.message.includes(state.url);
        pushNotification({
          message: isUriError
            ? "L'URL saisie est déjà utilisée dans une autre page orpheline"
            : 'Erreur durant la sauvegarde de la page',
          type: NotificationTypeEnum.error,
        });
      });
  };

  const additional = (
    <>
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Button
            onClick={doUpsert}
            data-testid="save-btn"
            variant="outlined"
            sx={{
              color: 'common.white',
              borderColor: 'common.white',
              marginRight: '1rem',
            }}
          >
            Sauvegarder
          </Button>
          <OrphanPageSelector
            currentStatus={state.status}
            changeStatus={(newStatus) => {
              setState({ ...state, status: newStatus });
            }}
          />
        </>
      )}
    </>
  );

  return (
    <>
      <Box sx={{ marginTop: '120px' }}>
        <EditAppBar
          additionalElement={additional}
          onNavigateBack={() => window.history.back()}
          title={
            <Box sx={{ position: 'relative' }}>
              <Grid container spacing={0.5} alignItems="center">
                <Grid item style={{ maxWidth: '70%' }}>
                  {loading ? (
                    <Skeletons width={200} height={14} />
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {state.title}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={3}>
                  <Chip
                    sx={{
                      marginLeft: 1,
                      color: 'common.white',
                      position: 'relative',
                    }}
                    label={state.brandKey}
                    variant="filled"
                    size="small"
                  />
                </Grid>

                <Grid container item>
                  {loading ? (
                    <Skeletons width={350} height={12} />
                  ) : (
                    state.publishedAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          opacity: 0.75,
                          overflow: 'hidden',
                          position: 'relative',
                          textOverflow: 'ellipsis',
                          top: -0.25,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {state.publishedAt
                          ? `Publié le ${formatDate(
                              new Date(state.publishedAt),
                              'dd/MM/yyy à HH:mm',
                            )}`
                          : ''}
                      </Typography>
                    )
                  )}
                </Grid>
              </Grid>
            </Box>
          }
        />
      </Box>
      <Box
        data-testid="orphan-pages-edit--container"
        sx={{
          margin: 'auto',
          width: '80%',
        }}
      >
        <OrphanEditor setState={setState} state={state} />
      </Box>
    </>
  );
};
